import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAppDispatch } from '@/redux/hooks';
import { sumbitAcceptItemRemovalResponse } from '@/redux/modules/itemRemovalResponse';
import { openFeedbackModal } from '@/redux/modules/modal';
import { parseQueryParams, removeQueryParams, stringifyQuery } from '@/utils/queryParams';
import { getHomeUrl } from '@/utils/urls';

type EOAQueryParams = {
    action?: 'remove-accept' | 'remove-decline';
    /** Lot ID */
    l?: string;
    /** Timestamp */
    ts?: string;
    /** Verification Code */
    v?: string;
};

/**
 * Handles direct links to won-items page from mainhost with EOA removal query params in URL
 *
 * Either accepts or rejects EOA removal request, based on query params, if they exist
 */
const EOARemovalResponseAction = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { formatMessage } = useIntl();

    const queryParams: EOAQueryParams = React.useMemo(
        () => parseQueryParams<EOAQueryParams>(location.search),
        [location.search]
    );

    const removeEOAQueryParamsFromPath = React.useCallback(
        (basePath: string) => {
            const query = removeQueryParams(queryParams, ['action', 'l', 'ts', 'v']);
            const stringified = stringifyQuery(query);
            const newLocation = `${basePath}${stringified ? `?${stringified}` : ''}`;
            return newLocation;
        },
        [queryParams]
    );

    React.useEffect(() => {
        // Accept EOA removal
        if (queryParams.action === 'remove-accept') {
            const { l: lotId, ts: timestamp, v: verificationCode } = queryParams;
            dispatch(sumbitAcceptItemRemovalResponse('accept', lotId, verificationCode, timestamp, formatMessage));

            // Redirect to won-items page without query params
            const newLocation = removeEOAQueryParamsFromPath(location.pathname);
            navigate(newLocation);
        }

        // Decline EOA removal
        if (queryParams.action === 'remove-decline') {
            const { l: lotId, ts: timestamp, v: verificationCode } = queryParams;
            dispatch(sumbitAcceptItemRemovalResponse('decline', lotId, verificationCode, timestamp, formatMessage));
            dispatch(openFeedbackModal());

            // Redirect to home page
            const newLocation = removeEOAQueryParamsFromPath(getHomeUrl());
            navigate(newLocation);
        }
    }, [dispatch, navigate, location.pathname, queryParams, removeEOAQueryParamsFromPath, formatMessage]);

    return null;
};

export default EOARemovalResponseAction;
